import { useState, useEffect, useRef, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'
import { TREATMENT_SLUGS } from '@/lib/treatments'
import type { SupportedLanguage } from '@/i18n'

interface NavItem {
  key: RouteKey
  labelKey: string
  children?: { key: RouteKey; labelKey: string }[]
}

const serviceRoutes: RouteKey[] = [
  'breastSurgery',
  'bodySurgery',
  'faceSurgery',
  'regenerative',
  'faceAesthetic',
  'bodyAesthetic',
  'laser',
]

const navItems: NavItem[] = [
  { key: 'home', labelKey: 'nav.home' },
  {
    key: 'breastSurgery',
    labelKey: 'nav.services',
    children: serviceRoutes.map((key) => ({
      key,
      labelKey: `services.${key}`,
    })),
  },
  { key: 'blog', labelKey: 'nav.blog' },
  { key: 'contacts', labelKey: 'nav.contacts' },
]

export function Header() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [isDesktopServicesOpen, setIsDesktopServicesOpen] = useState(false)
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false)
  const [activeCategory, setActiveCategory] = useState<RouteKey | null>(null)
  const [openMobileCategory, setOpenMobileCategory] = useState<RouteKey | null>(null)
  const [treatmentNamespacesReady, setTreatmentNamespacesReady] = useState(false)
  const servicesRef = useRef<HTMLDivElement>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const lang = i18n.language as SupportedLanguage

  const serviceTreatments = useMemo(() => {
    const map = {} as Record<RouteKey, string[]>
    serviceRoutes.forEach((key) => {
      map[key] = []
    })
    Object.entries(TREATMENT_SLUGS[lang]).forEach(([slug, categoryKey]) => {
      if (serviceRoutes.includes(categoryKey)) {
        map[categoryKey].push(slug)
      }
    })
    return map
  }, [lang])

  function getTreatmentTitle(slug: string, categoryKey: RouteKey): string {
    if (!treatmentNamespacesReady) return slug
    // Prefer the card title from the category page bundle
    const categoryBundle = i18n.getResourceBundle(lang, categoryKey) as
      | { treatments?: { items?: { slug: string; title: string }[] } }
      | undefined
    const cardTitle = categoryBundle?.treatments?.items?.find((item) => item.slug === slug)?.title
    if (cardTitle) return cardTitle
    // Fallback to treatment page bundle
    const bundle = i18n.getResourceBundle(lang, slug) as
      | { title?: string; hero?: { title?: string } }
      | undefined
    return bundle?.hero?.title || bundle?.title || slug
  }

  function getTreatmentRelativePath(categoryKey: RouteKey, treatmentSlug: string): string {
    const categorySlug = localizedRoutes[categoryKey][lang]
    return `${categorySlug}/${treatmentSlug}`
  }

  // Load treatment + category namespaces when a services dropdown is opened
  useEffect(() => {
    if (isDesktopServicesOpen || isMobileServicesOpen) {
      setActiveCategory((prev) => prev || serviceRoutes[0])
      const treatmentSlugs = Object.keys(TREATMENT_SLUGS[lang])
      i18n.loadNamespaces([...serviceRoutes, ...treatmentSlugs]).then(() => {
        setTreatmentNamespacesReady(true)
      })
    }
  }, [isDesktopServicesOpen, isMobileServicesOpen, lang, i18n])

  const pathSegments = location.pathname.split('/').filter(Boolean)
  const currentSlug = pathSegments[1] || ''

  // Detect scroll
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false)
    setIsMobileServicesOpen(false)
    setIsDesktopServicesOpen(false)
    setIsScrolled(false)
  }, [location.pathname])

  // Reset mobile service accordion when mobile menu closes
  useEffect(() => {
    if (!isMobileOpen) {
      setIsMobileServicesOpen(false)
      setOpenMobileCategory(null)
    }
  }, [isMobileOpen])

  // Lock body scroll while mobile menu is open by fixing it in place
  useEffect(() => {
    if (!isMobileOpen) return
    const scrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    return () => {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
    }
  }, [isMobileOpen])

  // Close desktop services dropdown on click outside
  useEffect(() => {
    if (!isDesktopServicesOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setIsDesktopServicesOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDesktopServicesOpen])

  // Force opaque header while mobile menu is open so it never looks transparent.
  const isTransparent = !isScrolled && !isMobileOpen

  const handleServicesEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    setIsDesktopServicesOpen(true)
  }

  const handleServicesLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsDesktopServicesOpen(false)
    }, 150)
  }

  const isActive = (key: RouteKey) => {
    const routePath = localizedRoutes[key]
    if (key === 'home') return currentSlug === ''
    if (key === 'breastSurgery') {
      return serviceRoutes.some(
        (serviceKey) =>
          localizedRoutes[serviceKey].it === currentSlug ||
          localizedRoutes[serviceKey].en === currentSlug
      )
    }
    return routePath.it === currentSlug || routePath.en === currentSlug
  }

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-colors duration-300',
        isMobileOpen
          ? 'bg-ink-deep shadow-lg border-b border-white/10'
          : isTransparent
            ? 'bg-transparent'
            : 'bg-ink-deep/95 backdrop-blur-md shadow-lg'
      )}
    >
      <nav
        aria-label={t('aria.mainNavigation', 'Main navigation')}
        className="mx-auto relative flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 h-16 md:h-20"
      >
        {/* Logo */}
        <div className="flex-1 flex justify-start">
          <LocalizedLink to="home" className="flex-shrink-0">
            <img
              src="/media/logos/LogoADAesthetics-monotone.svg"
              alt="AD Aesthetics"
              className="h-10 md:h-12 w-auto"
            />
          </LocalizedLink>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center gap-1">
          {navItems.map(({ key, labelKey, children }) => {
            const active = isActive(key)

            if (children) {
              return (
                <div
                  key={key}
                  ref={servicesRef}
                  className="relative"
                  onMouseEnter={handleServicesEnter}
                  onMouseLeave={handleServicesLeave}
                >
                  <button
                    onClick={() => setIsDesktopServicesOpen(!isDesktopServicesOpen)}
                    aria-expanded={isDesktopServicesOpen}
                    aria-haspopup="true"
                    className={cn(
                      'group relative flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      'text-white/90 hover:text-white hover:bg-white/10',
                      active && 'text-white'
                    )}
                  >
                    {t(labelKey)}
                    <ChevronDown
                      className={cn(
                        'w-4 h-4 transition-transform duration-200',
                        isDesktopServicesOpen && 'rotate-180'
                      )}
                    />
                  </button>

                  <AnimatePresence>
                    {isDesktopServicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[760px] max-w-[calc(100vw-2rem)] bg-ink-deep/98 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl overflow-hidden"
                        onMouseEnter={handleServicesEnter}
                        onMouseLeave={handleServicesLeave}
                      >
                        <div className="grid grid-cols-[220px_1fr]">
                          <div className="p-4 space-y-1">
                            {children.map((child) => (
                              <LocalizedLink
                                key={child.key}
                                to={child.key}
                                className={cn(
                                  'group flex items-center justify-between px-4 py-3 rounded-lg transition-colors',
                                  activeCategory === child.key
                                    ? 'bg-white/10 text-white'
                                    : 'text-white/80 hover:bg-white/5 hover:text-white'
                                )}
                                onMouseEnter={() => setActiveCategory(child.key)}
                                onFocus={() => setActiveCategory(child.key)}
                              >
                                <span className="font-medium">{t(child.labelKey)}</span>
                                <ChevronRight
                                  className={cn(
                                    'w-4 h-4 transition-all',
                                    activeCategory === child.key
                                      ? 'text-primary opacity-100 translate-x-0'
                                      : 'opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0'
                                  )}
                                />
                              </LocalizedLink>
                            ))}
                          </div>

                          <div className="bg-white/5 p-6 border-l border-white/10 min-h-[280px]">
                            {activeCategory && serviceTreatments[activeCategory]?.length > 0 && (
                              <>
                                <h3 className="text-xs uppercase tracking-widest text-primary mb-4 font-semibold">
                                  {t(`services.${activeCategory}`, '')}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                                  {serviceTreatments[activeCategory].map((slug) => (
                                    <LocalizedLink
                                      key={slug}
                                      to={getTreatmentRelativePath(activeCategory, slug)}
                                      className="group flex items-center py-2 text-sm text-white/70 hover:text-primary transition-colors"
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full bg-primary/50 mr-2.5 group-hover:bg-primary transition-colors" />
                                      {getTreatmentTitle(slug, activeCategory)}
                                    </LocalizedLink>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            }

            return (
              <LocalizedLink
                key={key}
                to={key}
                className={cn(
                  'group relative px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  'text-white/90 hover:text-white hover:bg-white/10',
                  active && 'text-white font-semibold'
                )}
              >
                {t(labelKey)}
                <span
                  className={cn(
                    'absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-primary transition-all',
                    active
                      ? 'scale-x-100 opacity-100'
                      : 'scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100'
                  )}
                />
              </LocalizedLink>
            )
          })}
        </div>

        {/* Desktop Actions */}
        <div className="hidden lg:flex flex-1 justify-end items-center gap-3">
          <LocalizedLink to="bookConsultation">
            <span className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-primary text-ink hover:bg-primary-dark transition-colors">
              {t('buttons.bookConsultation', 'Prenota consulenza')}
            </span>
          </LocalizedLink>
          <LanguageSelector variant="desktop" isScrolled={!isTransparent} dark />
        </div>

        {/* Mobile Actions */}
        <div className="lg:hidden flex items-center gap-1">
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="relative z-50 p-2 rounded-md transition-colors text-white hover:bg-white/10"
            aria-label={isMobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
            aria-expanded={isMobileOpen}
          >
            {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Navigation Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 top-16 md:top-20 bg-ink-deep z-40 overflow-y-auto"
        >
            <nav
              className="flex flex-col py-4"
              aria-label={t('aria.mobileNavigation', 'Mobile navigation')}
            >
              {navItems.map(({ key, labelKey, children }) => {
                const active = isActive(key)

                if (children) {
                  return (
                    <div key={key}>
                      <button
                        onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                        className={cn(
                          'w-full flex items-center justify-between px-6 py-3 text-lg font-medium transition-colors text-white',
                          active && 'text-primary'
                        )}
                        aria-expanded={isMobileServicesOpen}
                      >
                        {t(labelKey)}
                        <ChevronDown
                          className={cn(
                            'w-5 h-5 transition-transform duration-200',
                            isMobileServicesOpen && 'rotate-180'
                          )}
                        />
                      </button>
                      {isMobileServicesOpen && (
                        <div className="overflow-hidden bg-white/5">
                            {children.map((child) => {
                              const isOpen = openMobileCategory === child.key
                              return (
                                <div key={child.key} className="border-b border-white/5 last:border-b-0">
                                  <div className="flex items-center justify-between">
                                    <LocalizedLink
                                      to={child.key}
                                      className="flex-1 px-8 py-3 text-base text-white/90 hover:text-primary hover:bg-white/5 transition-colors"
                                      onClick={() => setIsMobileOpen(false)}
                                    >
                                      {t(child.labelKey)}
                                    </LocalizedLink>
                                    <button
                                      onClick={() =>
                                        setOpenMobileCategory(isOpen ? null : child.key)
                                      }
                                      className="px-4 py-3 text-white/60 hover:text-white transition-colors"
                                      aria-expanded={isOpen}
                                      aria-label={`${t('aria.showTreatments', 'Mostra trattamenti')} ${t(child.labelKey)}`}
                                    >
                                      <ChevronDown
                                        className={cn(
                                          'w-5 h-5 transition-transform duration-200',
                                          isOpen && 'rotate-180'
                                        )}
                                      />
                                    </button>
                                  </div>
                                  {isOpen && (
                                    <div className="px-8 pb-3 space-y-1">
                                      {serviceTreatments[child.key]?.map((slug) => (
                                        <LocalizedLink
                                          key={slug}
                                          to={getTreatmentRelativePath(child.key, slug)}
                                          className="block py-2 text-sm text-white/70 hover:text-primary transition-colors"
                                          onClick={() => setIsMobileOpen(false)}
                                        >
                                          {getTreatmentTitle(slug, child.key)}
                                        </LocalizedLink>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                    </div>
                  )
                }

                return (
                  <LocalizedLink
                    key={key}
                    to={key}
                    className={cn(
                      'px-6 py-3 text-lg font-medium transition-colors',
                      active
                        ? 'text-ink bg-primary'
                        : 'text-white hover:bg-white/5'
                    )}
                  >
                    {t(labelKey)}
                  </LocalizedLink>
                )
              })}
              <div className="px-6 py-4">
                <LocalizedLink to="bookConsultation" className="block w-full">
                  <span className="block w-full text-center px-4 py-3 text-sm font-medium rounded-lg bg-primary text-ink hover:bg-primary-dark transition-colors">
                    {t('buttons.bookConsultation', 'Prenota consulenza')}
                  </span>
                </LocalizedLink>
              </div>
              <div className="border-t border-white/10 mt-2 pt-4 px-6">
                <LanguageSelector variant="mobile" />
              </div>
            </nav>
          </div>
        )}
    </header>
  )
}
